
import React, { useState, useEffect, useCallback } from "react";
import Header from "./header/header";
import Footer from "./Footer";
import "./Blogs.css";
import { ClipLoader } from "react-spinners";
import BlogCards from "./BlogCards";
import axios from "axios";
import ReactPaginate from "react-paginate";
import { baseUrl, GetToken } from "../App";
import { BlogType } from "./type";

const Blogs: React.FC = () => {
  const [originalData, setOriginalData] = useState([]);
  const [result, setResult] = useState([]);
  const [input, setInput] = useState("");
  const perPage = 5; // Number of blog cards per page
  const [currentPage, setCurrentPage] = useState(0);
  const startIndex = currentPage * perPage;
  const endIndex = startIndex + perPage;
  const displayedBlogs = result.slice(startIndex, endIndex);
  const [ready, setReady] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [error, setError] = useState(true);
  const [clickedButtonIndex, setClickedButtonIndex] = useState<string | null>("All");

  const getResult = useCallback(async () => {
    if (!isDataFetched) {
      const idToken = await GetToken();
      await axios
        .get(`${baseUrl}/blog-posts`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        })
        .then((response) => {
          const data = response.data?.filter((each: any) => each.isDeleted === false);
          
          // Date sorting
          const sortedData = data.sort((a: any, b: any) => {
            const dateA = a.Date ? parseDate(a.Date) : null;
            const dateB = b.Date ? parseDate(b.Date) : null;

            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;

            return dateB.getTime() - dateA.getTime();
          });

          function parseDate(dateString: string): Date {
            const [day, month, year] = dateString.split("/");
            return new Date(Number(year), Number(month) - 1, Number(day));
          }

          const newData = sortedData.filter(
            (element: BlogType) => element?.Display === true
          );
          setResult(newData);
          setOriginalData(newData);
          setIsDataFetched(true);
          setReady(true);
        })
        .catch((error) => {
          console.error("Error fetching blog posts:", error);
        });
    }
  }, [isDataFetched]);

  const loadings = () => {
    new Promise((r) => setTimeout(r, 205));
    setError(true);
  };

  useEffect(() => {
    getResult();
    loadings();
  }, [getResult]);

  const handlefilterInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.toLowerCase();
    setInput(inputValue);

    if (inputValue === " ") {
      setResult(originalData);
    } else {
      const filteredData = originalData.filter((item: BlogType) =>
        item.Title?.toLowerCase().includes(inputValue)
      );
      setResult(filteredData);
    }
  };

  const handleButton = (value: string) => {
    setResult(originalData);
    setClickedButtonIndex(value);
    if (value === "All") {
      setResult(originalData);
    } else {
      const filteredData = originalData.filter(
        (item: BlogType) => item.Category === value
      );
      setResult(filteredData);
    }
  };

  // YOUR ORIGINAL TECH CATEGORIES
  const categories = [
    { label: "All", value: "All" },
    { label: "AR/VR", value: "AR/VR" },
    { label: "Data", value: "Data" },
    { label: "Design", value: "Design" },
    { label: "Product", value: "Product" },
    { label: "Software", value: "Software" },
    { label: "ML", value: "Machine Learning" },
    { label: "AI", value: "AI" },
    { label: "Hardware", value: "Hardware" },
  ];

  return (
    <div>
      {ready ? (
        <>
          <Header />

          {/* HERO & FILTER SECTION */}
          <div className="w-full bg-white flex flex-col items-center justify-center text-center pt-12 pb-4 px-4 max-w-4xl mx-auto">
            {/* Centered Title - Green */}
            <h1 className="text-4xl md:text-5xl font-extrabold text-[#047E35] tracking-tight">
              Our Blog
            </h1>

            {/* Pill Search Input - Gap reduced using mt-4 */}
            <div className="relative mt-4 w-full max-w-xl">
              <input
                type="text"
                placeholder="Search articles..."
                className="w-full px-6 py-3 bg-white border border-gray-200 rounded-full text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#047E35]/20 focus:border-[#047E35] transition-all shadow-sm pr-12"
                value={input}
                onChange={handlefilterInput}
              />
              <div className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Clean Green Pill Category Filters - Gap reduced using mt-4 */}
            <div className="mt-4 flex flex-wrap justify-center gap-2.5 w-full">
              {categories.map((cat) => {
                const isActive = clickedButtonIndex === cat.value;
                return (
                  <button
                    key={cat.value}
                    value={cat.value}
                    onClick={() => handleButton(cat.value)}
                    className={`px-5 py-2 text-sm font-medium rounded-full border transition-all duration-200 ${
                      isActive
                        ? "bg-[#047E35] text-white border-[#047E35] shadow-sm"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
            
            {/* Post Count Feedback */}
            <div className="mt-4 text-xs text-gray-400 font-medium tracking-wide">
              {error && `(${result.length}) blog posts found`}
            </div>
          </div>

          {/* BLOG CARDS CONTAINER */}
          <div className="blogcard max-w-5xl mx-auto px-4 py-6 flex flex-col gap-8">
            {displayedBlogs.map((blog, index) => (
              <BlogCards key={index} blog={blog} />
            ))}
            
            <div className="pagination mt-4">
              <ReactPaginate
                pageCount={Math.ceil(result.length / perPage)}
                pageRangeDisplayed={2}
                marginPagesDisplayed={1}
                onPageChange={(data) => setCurrentPage(data.selected)}
                containerClassName={"pagination"}
                activeClassName={"active"}
              />
            </div>
          </div>

          <Footer />
        </>
      ) : (
        <>
          <div>
            <div className="academystyle">
              <img src="../../images/doyenifyAcademy.png" alt="doy academy" />
              <ClipLoader size={25} color="#047E35" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Blogs;